
import { defineComponent, PropType } from 'vue'

import { shortenLargeNumber } from '~/utils/string'
import { checkStatusIcon, checkStatusIconColor } from '~/utils/ui'

import type { Check, ScaCheck } from '~/types/types'
import { extractHashId } from '~/utils/hashId'

/**
 * Sidebar component to navigate between different analyzers for a given run
 *
 */
export default defineComponent({
  name: 'AnalyzerSelector',
  props: {
    checks: {
      type: Array as PropType<Array<Check>>,
      default: () => []
    },
    scaChecks: {
      type: Array as PropType<Array<ScaCheck>>,
      default: () => []
    }
  },
  computed: {
    /**
     * Get currently active analyzer
     *
     * @returns {string} the shortcode of the currently active analyzer
     */
    currentAnalyzer(): string | undefined {
      return this.$route.params.analyzer
    },
    checkGroups() {
      return this.checks.reduce(
        (acc, check) => {
          const shortcode = check.analyzer?.shortcode
          const group =
            shortcode === 'secrets' || shortcode === 'test-coverage' ? 'static' : 'code-analysis'

          acc[group].push(check)
          return acc
        },
        { static: [], 'code-analysis': [] } as Record<string, Check[]>
      )
    }
  },
  methods: {
    shortenLargeNumber,
    /**
     * Get route for a given analyzer shortcode.
     *
     * @param {string} shortcode Shortcode of an analyzer
     * @returns {string} Route to given analyzer's check
     */
    getRoute(shortcode: string): string {
      const { runId, issueId } = this.$route.params
      let link = this.$generateRoute(['run', runId, shortcode])
      if (this.currentAnalyzer === shortcode) {
        if (issueId && this.$route.query) {
          const { listsort, listcategory, listq } = this.$route.query

          const filters = []
          if (listsort) filters.push(`sort=${listsort}`)
          if (listcategory) filters.push(`category=${listcategory}`)
          if (listq) filters.push(`q=${listq}`)
          if (filters.length > 0) {
            link = `${link}?${filters.join('&')}`
          }
        }
      }
      return link
    },
    getScaRoute(targetId: string) {
      const targetHash = extractHashId(targetId)
      const { runId } = this.$route.params

      return this.$generateRoute(['run', runId, 'sca', targetHash])
    },
    getCheckProps(check: Check) {
      return {
        icon: checkStatusIcon(check.status),
        iconColor: checkStatusIconColor(check.status),
        active: this.currentAnalyzer === check.analyzer?.shortcode,
        isCollapsed: false,
        isVisible: true,
        to: check.analyzer ? this.getRoute(check.analyzer.shortcode) : ''
      }
    },
    getScaCheckProps(scaCheck: ScaCheck) {
      return {
        icon: checkStatusIcon(scaCheck.status),
        iconColor: checkStatusIconColor(scaCheck.status),
        active:
          this.$route.name?.includes('sca') &&
          this.$route.params.targetId &&
          scaCheck.target?.id &&
          this.$route.params.targetId === extractHashId(scaCheck.target.id),
        isCollapsed: false,
        isVisible: true,
        to: scaCheck.target?.id ? this.getScaRoute(scaCheck.target.id) : ''
      }
    }
  }
})
